import type React from "react";
import { useEffect, useState, useCallback, useRef } from "react";
import { Spinner } from "@cynaps/ui";
import { AnnotationTestTask, TestResultsDisplay } from "./components";
import type { TestCase, AnnotationResult } from "./data/testCases";
import type { TestResult } from "./data/scoring";
import "./AnnotatorSkillTest.css";

// ============== TYPES ==============
interface LangOption {
  code: string;
  name: string;
}

interface SpecInfo {
  id: number;
  name: string;
  slug: string;
  template_folder: string;
  passing_score: number;
  project_count: number;
  task_count: number;
  honeypot_count: number;
  available_languages?: LangOption[];
}

interface CategoryInfo {
  id: number;
  name: string;
  slug: string;
  template_folder: string;
  project_count: number;
  task_count: number;
  specializations: SpecInfo[];
}

interface APITestTask {
  task_id: number;
  project_id: number;
  project_title: string;
  label_config: string;
  data: Record<string, any>;
  difficulty: "easy" | "medium" | "hard";
  points: number;
  has_ground_truth: boolean;
  ground_truth: any;
  predictions?: Array<{ result: any[] }>;
}

type Phase = "browse" | "testing" | "results";

function apiTaskToTestCase(apiTask: APITestTask, index: number): TestCase {
  return {
    id: String(apiTask.task_id),
    specialty: "debug",
    title: apiTask.project_title,
    description: `Annotation task ${index + 1}: ${apiTask.project_title}`,
    difficulty: apiTask.difficulty,
    points: apiTask.points,
    config: apiTask.label_config,
    task: {
      data: apiTask.data,
      predictions: apiTask.predictions || [],
    },
    groundTruth: [],
    scoringCriteria: { type: "exact" },
  };
}

// ============== LANGUAGE PICKER MODAL ==============
const LanguagePickerModal: React.FC<{
  languages: LangOption[];
  onConfirm: (langA: string, langB: string) => void;
  onCancel: () => void;
}> = ({ languages, onConfirm, onCancel }) => {
  const [langA, setLangA] = useState("");
  const [langB, setLangB] = useState("");

  const canConfirm = langA && langB && langA !== langB;

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ color: "#fff", margin: "0 0 0.5rem" }}>Select Your Languages</h3>
        <p style={{ color: "#9ca3af", fontSize: "0.88rem", margin: "0 0 1.25rem" }}>
          Pick two languages you are proficient in. The test will include translations in both directions.
        </p>

        <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem" }}>
          <div style={{ flex: 1 }}>
            <label style={styles.modalLabel}>Language 1</label>
            <select style={styles.modalSelect} value={langA} onChange={(e) => setLangA(e.target.value)}>
              <option value="">— select —</option>
              {languages.map((l) => (
                <option key={l.code} value={l.code} disabled={l.code === langB}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={styles.modalLabel}>Language 2</label>
            <select style={styles.modalSelect} value={langB} onChange={(e) => setLangB(e.target.value)}>
              <option value="">— select —</option>
              {languages.map((l) => (
                <option key={l.code} value={l.code} disabled={l.code === langA}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {langA && langB && langA === langB && (
          <p style={{ color: "#f87171", fontSize: "0.82rem", margin: "0 0 1rem" }}>
            Please select two different languages.
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
          <button style={styles.btnSecondary} onClick={onCancel}>
            Cancel
          </button>
          <button
            style={{ ...styles.btnPrimary, opacity: canConfirm ? 1 : 0.4 }}
            disabled={!canConfirm}
            onClick={() => onConfirm(langA, langB)}
          >
            Start Test ▶
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== BROWSE PANEL ==============
const BrowsePanel: React.FC<{
  categories: CategoryInfo[];
  loading: boolean;
  onStartTest: (catSlug: string, specSlug?: string, languagePair?: string) => void;
}> = ({ categories, loading, onStartTest }) => {
  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [langPickerSpec, setLangPickerSpec] = useState<{ catSlug: string; spec: SpecInfo } | null>(null);

  if (loading) {
    return (
      <div style={styles.center}>
        <Spinner size={48} />
        <p style={styles.subtitle}>Loading categories...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🛠️ Debug Test Samples</h1>
        <p style={styles.subtitle}>
          Click a specialization to start a debug test instantly — no email or token needed.
        </p>
      </div>

      <div style={styles.catGrid}>
        {categories.map((cat) => (
          <div key={cat.id} style={styles.catCard}>
            <div style={styles.catHeader} onClick={() => setExpandedCat(expandedCat === cat.slug ? null : cat.slug)}>
              <div>
                <h2 style={styles.catName}>{cat.name}</h2>
                <span style={styles.catMeta}>
                  {cat.project_count} projects · {cat.task_count} tasks · {cat.specializations.length} specs
                </span>
              </div>
              <button
                style={styles.btnSecondary}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartTest(cat.slug);
                }}
                title="Test all tasks in this category"
              >
                Test All ▶
              </button>
            </div>

            {expandedCat === cat.slug && (
              <div style={styles.specList}>
                {cat.specializations.length === 0 && <p style={styles.noSpecs}>No specializations configured</p>}
                {cat.specializations.map((spec) => (
                  <div key={spec.id} style={styles.specRow}>
                    <div style={styles.specInfo}>
                      <span style={styles.specName}>{spec.name}</span>
                      <span style={styles.specMeta}>
                        {spec.project_count} proj · {spec.task_count} tasks · {spec.honeypot_count} honeypots · pass:{" "}
                        {spec.passing_score}%
                      </span>
                    </div>
                    <button
                      style={{
                        ...styles.btnPrimary,
                        opacity: spec.task_count === 0 ? 0.4 : 1,
                      }}
                      disabled={spec.task_count === 0}
                      onClick={() => {
                        if (spec.available_languages && spec.available_languages.length > 0) {
                          setLangPickerSpec({ catSlug: cat.slug, spec });
                        } else {
                          onStartTest(cat.slug, spec.slug);
                        }
                      }}
                    >
                      Test ▶
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {langPickerSpec && (
        <LanguagePickerModal
          languages={langPickerSpec.spec.available_languages!}
          onConfirm={(langA, langB) => {
            onStartTest(langPickerSpec.catSlug, langPickerSpec.spec.slug, `${langA},${langB}`);
            setLangPickerSpec(null);
          }}
          onCancel={() => setLangPickerSpec(null)}
        />
      )}
    </div>
  );
};

// ============== DEBUG INFO BAR ==============
const DebugInfoBar: React.FC<{
  task: APITestTask;
  index: number;
  total: number;
}> = ({ task, index, total }) => (
  <div style={styles.debugBar}>
    <span style={styles.debugLabel}>DEBUG</span>
    <span>
      Task {index + 1}/{total}
    </span>
    <span>·</span>
    <span>ID: {task.task_id}</span>
    <span>·</span>
    <span>Project: {task.project_id}</span>
    <span>·</span>
    <span>GT: {task.has_ground_truth ? "✅" : "❌"}</span>
    {task.has_ground_truth && task.ground_truth && (
      <>
        <span>·</span>
        <span style={styles.gtPreview} title={JSON.stringify(task.ground_truth, null, 2)}>
          🔍 Hover for GT
        </span>
      </>
    )}
    <span>·</span>
    <span style={styles.dataKeys} title={JSON.stringify(task.data, null, 2)}>
      data: {"{" + Object.keys(task.data).join(", ") + "}"}
    </span>
  </div>
);

// ============== MAIN COMPONENT ==============
export const DebugTestSamples: React.FC & { title?: string; path?: string; exact?: boolean } = () => {
  const [phase, setPhase] = useState<Phase>("browse");
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiTasks, setApiTasks] = useState<APITestTask[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const answersRef = useRef<Map<string, AnnotationResult[]>>(new Map());
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testMeta, setTestMeta] = useState<{ category: string; specialization: string; passing: number }>({
    category: "",
    specialization: "",
    passing: 70,
  });
  const [taskStartedAt, setTaskStartedAt] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(1800);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Load categories on mount
  useEffect(() => {
    fetch("/api/annotators/expertise/debug-test-samples", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setCategories(data.categories || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load categories:", err);
        setLoading(false);
      });
  }, []);

  // Timer
  useEffect(() => {
    if (phase === "testing" && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [phase, timeRemaining]);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    [],
  );

  // Start test
  const handleStartTest = useCallback(async (catSlug: string, specSlug?: string, languagePair?: string) => {
    setLoading(true);
    try {
      const body: any = { category_slug: catSlug, max_tasks: 10 };
      if (specSlug) body.specialization_slug = specSlug;
      if (languagePair) body.language_pair = languagePair;

      const resp = await fetch("/api/annotators/expertise/debug-test-samples", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) {
        alert(data.error || "Failed");
        setLoading(false);
        return;
      }

      const tasks: APITestTask[] = data.tasks || [];
      if (tasks.length === 0) {
        alert("No tasks returned");
        setLoading(false);
        return;
      }

      setApiTasks(tasks);
      setTestCases(tasks.map((t, i) => apiTaskToTestCase(t, i)));
      setTestMeta({
        category: data.category,
        specialization: data.specialization,
        passing: data.passing_score || 70,
      });
      setCurrentTaskIndex(0);
      answersRef.current.clear();
      setTimeRemaining((data.time_limit_minutes || 30) * 60);
      setTaskStartedAt(Date.now());
      setPhase("testing");
    } catch (err) {
      console.error(err);
      alert("Error starting test");
    } finally {
      setLoading(false);
    }
  }, []);

  // Task complete
  const handleTaskComplete = useCallback(
    (result: AnnotationResult[]) => {
      const currentTask = testCases[currentTaskIndex];
      answersRef.current.set(currentTask.id, result);

      if (currentTaskIndex < testCases.length - 1) {
        setCurrentTaskIndex((prev) => prev + 1);
      } else {
        finishTest();
      }
    },
    [currentTaskIndex, testCases],
  );

  // Skip
  const handleSkipTask = useCallback(() => {
    const currentTask = testCases[currentTaskIndex];
    answersRef.current.set(currentTask.id, []);
    if (currentTaskIndex < testCases.length - 1) {
      setCurrentTaskIndex((prev) => prev + 1);
    } else {
      finishTest();
    }
  }, [currentTaskIndex, testCases]);

  // Finish — local grading for debug (since we have GT exposed)
  const finishTest = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const timeTaken = Math.floor((Date.now() - taskStartedAt) / 1000);

    let totalPoints = 0;
    let earnedPoints = 0;
    const taskScores = apiTasks.map((at) => {
      const answers = answersRef.current.get(String(at.task_id)) || [];
      const pts = at.points;
      totalPoints += pts;

      // Simple scoring: if answers submitted, give proportional credit
      let earned = 0;
      if (answers.length > 0) {
        if (at.has_ground_truth) {
          // Check if annotation labels match ground truth
          earned = pts * 0.7; // Base credit for submitting with GT task
          const gt = at.ground_truth;
          if (gt) {
            const gtLabels = gt.labels || gt.label ? [gt.label || gt.labels].flat() : [];
            const ansLabels = answers.flatMap(
              (a: any) => a.value?.choices || a.value?.labels || (a.value?.text ? [a.value.text] : []),
            );
            if (gtLabels.length > 0 && ansLabels.length > 0) {
              const matches = ansLabels.filter((l: string) =>
                gtLabels.some((g: string) => String(g).toLowerCase() === String(l).toLowerCase()),
              );
              if (matches.length > 0) earned = pts * 1.0;
            }
          }
        } else {
          earned = pts * 0.6; // No GT — credit for completing
        }
      }
      earnedPoints += earned;

      return {
        testCaseId: String(at.task_id),
        specialty: "debug",
        title: at.project_title,
        maxPoints: pts,
        earnedPoints: earned,
        percentage: pts > 0 ? Math.round((earned / pts) * 100) : 0,
        feedback:
          answers.length > 0
            ? at.has_ground_truth
              ? "Graded against ground truth"
              : "No GT — submission credit"
            : "Skipped",
        details: [],
      };
    });

    const pct = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;

    setTestResult({
      totalPoints,
      earnedPoints: Math.round(earnedPoints * 10) / 10,
      percentage: pct,
      passed: pct >= testMeta.passing,
      passingThreshold: testMeta.passing,
      taskScores,
      specialtyScores: {
        debug: {
          specialty: "debug",
          totalPoints,
          earnedPoints: Math.round(earnedPoints * 10) / 10,
          percentage: pct,
          taskCount: taskScores.length,
          passed: pct >= testMeta.passing,
        },
      },
      timeTaken,
    });
    setPhase("results");
  }, [apiTasks, testMeta, taskStartedAt]);

  // Back to browse
  const handleRetry = useCallback(() => {
    setPhase("browse");
    setTestResult(null);
    setApiTasks([]);
    setTestCases([]);
    setCurrentTaskIndex(0);
    answersRef.current.clear();
  }, []);

  // ============ RENDER ============
  if (phase === "browse") {
    return (
      <div className="annotator-skill-test" style={{ minHeight: "100vh" }}>
        <BrowsePanel categories={categories} loading={loading} onStartTest={handleStartTest} />
      </div>
    );
  }

  if (phase === "testing") {
    const currentTestCase = testCases[currentTaskIndex];
    const currentApiTask = apiTasks[currentTaskIndex];
    return (
      <div className="annotator-skill-test">
        <DebugInfoBar task={currentApiTask} index={currentTaskIndex} total={apiTasks.length} />
        <AnnotationTestTask
          key={currentTestCase.id}
          testCase={currentTestCase}
          onComplete={handleTaskComplete}
          onSkip={handleSkipTask}
          taskNumber={currentTaskIndex + 1}
          totalTasks={testCases.length}
          timeRemaining={timeRemaining}
        />
      </div>
    );
  }

  if (phase === "results" && testResult) {
    return (
      <div className="annotator-skill-test">
        <div style={styles.debugBar}>
          <span style={styles.debugLabel}>DEBUG RESULTS</span>
          <span>
            {testMeta.category} / {testMeta.specialization}
          </span>
          <span>·</span>
          <span>Passing: {testMeta.passing}%</span>
        </div>
        <TestResultsDisplay result={testResult} onRetry={handleRetry} onContinue={handleRetry} />
      </div>
    );
  }

  return null;
};

// ============== INLINE STYLES ==============
const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "2rem 1.5rem",
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
  },
  header: {
    marginBottom: "2rem",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    paddingBottom: "1rem",
  },
  title: {
    color: "#fff",
    fontSize: "1.75rem",
    margin: 0,
  },
  subtitle: {
    color: "#9ca3af",
    marginTop: "0.5rem",
    fontSize: "0.95rem",
  },
  catGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  catCard: {
    background: "rgba(255,255,255,0.04)",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  catHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem 1.25rem",
    cursor: "pointer",
  },
  catName: {
    color: "#e5e7eb",
    fontSize: "1.1rem",
    margin: 0,
  },
  catMeta: {
    color: "#6b7280",
    fontSize: "0.82rem",
  },
  specList: {
    padding: "0 1.25rem 1rem",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  specRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.6rem 0",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },
  specInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
  },
  specName: {
    color: "#d1d5db",
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  specMeta: {
    color: "#6b7280",
    fontSize: "0.78rem",
  },
  noSpecs: {
    color: "#6b7280",
    fontStyle: "italic" as const,
    padding: "0.5rem 0",
  },
  btnPrimary: {
    background: "rgba(124, 58, 237, 0.8)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.4rem 1rem",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  btnSecondary: {
    background: "rgba(255,255,255,0.08)",
    color: "#d1d5db",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "0.4rem 0.8rem",
    cursor: "pointer",
    fontSize: "0.82rem",
    whiteSpace: "nowrap",
  },
  debugBar: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    padding: "0.4rem 1rem",
    background: "rgba(234, 179, 8, 0.1)",
    borderBottom: "1px solid rgba(234, 179, 8, 0.25)",
    color: "#fbbf24",
    fontSize: "0.78rem",
    fontFamily: "monospace",
    flexWrap: "wrap",
    zIndex: 100,
    position: "sticky",
    top: 0,
  },
  debugLabel: {
    background: "rgba(234, 179, 8, 0.3)",
    padding: "0.15rem 0.5rem",
    borderRadius: 4,
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.05em",
  },
  gtPreview: {
    cursor: "pointer",
    textDecoration: "underline dotted",
  },
  dataKeys: {
    color: "#a3e635",
    cursor: "help",
  },
  modalOverlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    background: "#1f2937",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    padding: "1.75rem 2rem",
    width: 440,
    maxWidth: "90vw",
  },
  modalLabel: {
    display: "block",
    color: "#9ca3af",
    fontSize: "0.82rem",
    marginBottom: "0.35rem",
    fontWeight: 500,
  },
  modalSelect: {
    width: "100%",
    padding: "0.5rem 0.6rem",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(0,0,0,0.3)",
    color: "#e5e7eb",
    fontSize: "0.9rem",
    outline: "none",
  },
};
